// What a Jira connection is asked for, once (#599).
//
// The stepped flow that connects or edits one (`JiraFlow.tsx`, #599, #817) asks for these
// settings. Every label, hint and placeholder below is an inline literal pinned by an
// exact-string test, so what renders them renders these components rather than holding a
// copy: a copy drifts the first time one of them is reworded. The reasons
// for the words themselves are on #578, and for the token field's attributes on #571.

import type { ReactNode } from "react";

import type { NewJira } from "../api/integrations";
import { Field } from "./chrome";

/** Where an Atlassian API token is made: the one without scopes (#578). */
const ATLASSIAN_TOKENS = "https://id.atlassian.com/manage-profile/security/api-tokens";

const list = (text: string): string[] =>
  text
    .split(",")
    .map((item) => item.trim())
    .filter((item) => item !== "");

/** A Jira Cloud site: one DNS label under `atlassian.net`, which is all the server takes. */
const JIRA_SITE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.atlassian\.net$/;

/** A site address read back: the site alone, and whatever was dropped to get there. */
export type SiteAddress = { site: string; dropped: string | null } | { problem: string };

/**
 * The Jira site address a person typed, reduced to the site itself.
 *
 * People paste the page they are looking at — `…/jira/servicedesk/projects/TAT/
 * queues/custom/1` — and the server refuses the lot (`setup.py`, `JIRA_BASE_URL`).
 * The path is dropped and reported rather than refused, because dropping it is
 * always what was meant; a host that is not a Jira Cloud site is refused, and
 * the refusal names the host, because no repair of that one is safe to guess.
 */
export function jiraSiteAddress(raw: string): SiteAddress {
  const typed = raw.trim();
  if (typed === "") {
    return { problem: "Enter your Jira site address, for example https://rochwall.atlassian.net" };
  }
  let url: URL;
  try {
    url = new URL(/^[a-z][a-z0-9+.-]*:\/\//i.test(typed) ? typed : `https://${typed}`);
  } catch {
    return {
      problem: `“${typed}” is not a web address. A Jira site address looks like https://rochwall.atlassian.net`,
    };
  }
  const host = url.hostname.toLowerCase();
  if (!JIRA_SITE.test(host)) {
    return {
      problem: `A Jira Cloud site address ends in .atlassian.net, and “${host}” does not. Use the address you sign in to Jira at, for example https://rochwall.atlassian.net`,
    };
  }
  const rest = `${url.pathname}${url.search}${url.hash}`.replace(/^\/+/, "");
  return { site: `https://${host}`, dropped: rest === "" ? null : rest };
}

/**
 * What the site field holds and says once an address has been read: the site kept and
 * what was dropped, or the address as typed and why it was refused.
 */
export interface SettledSite {
  baseUrl: string;
  siteError: string | null;
  dropped: string | null;
}

export function settledSite(typed: string): SettledSite {
  const address = jiraSiteAddress(typed);
  if ("problem" in address) return { baseUrl: typed, siteError: address.problem, dropped: null };
  return { baseUrl: address.site, siteError: null, dropped: address.dropped };
}

/** What the Jira flow holds, connecting or editing. */
export interface JiraFields {
  baseUrl: string;
  projectKey: string;
  issueType: string;
  labels: string;
  email: string;
  token: string;
}

/** What the Jira form sends: its fields trimmed, the labels split on commas. */
export function jiraIntegration(fields: JiraFields): NewJira {
  const address = jiraSiteAddress(fields.baseUrl);
  return {
    kind: "jira",
    config: {
      base_url: "site" in address ? address.site : fields.baseUrl.trim(),
      project_key: fields.projectKey.trim(),
      issue_type: fields.issueType.trim(),
      labels: list(fields.labels),
    },
    credential: { email: fields.email.trim(), api_token: fields.token },
  };
}

const JIRA_SITE_HINT = "Just the address, ending in .atlassian.net — for example https://rochwall.atlassian.net";

/**
 * Where an Atlassian API token comes from, in Atlassian's own words (#578).
 *
 * One node, read by every place that says it — the token field and the flow's first
 * step — because it is one instruction: a person replacing a token makes it in exactly
 * the place a person creating one does.
 */
export const WHERE_A_TOKEN_IS_MADE: ReactNode = (
  <>
    Create one at{" "}
    <a href={ATLASSIAN_TOKENS} target="_blank" rel="noreferrer noopener">
      id.atlassian.com → Security → API tokens
    </a>
    , with Create API token — not Create API token with scopes, which only answers on a
    different host.
  </>
);

/** The same instruction under the token field, with what happens to the token after. */
const ATLASSIAN_TOKEN_HINT: ReactNode = <>{WHERE_A_TOKEN_IS_MADE} Stored encrypted; never shown again.</>;

/** What a group of fields is given: what they hold, and where a keystroke goes. */
interface Holding {
  fields: JiraFields;
  onType: (field: keyof JiraFields, value: string) => void;
}

/** The site address, with its repair reported under it and its refusal beside it. */
export function JiraSiteField({
  fields,
  onType,
  siteError,
  dropped,
  onLeave,
}: Holding & {
  siteError: string | null;
  dropped: string | null;
  /** The person left the field with something in it: read the address now. */
  onLeave: (typed: string) => void;
}) {
  const hint: ReactNode =
    dropped === null ? (
      JIRA_SITE_HINT
    ) : (
      <>Kept the site address and dropped “{dropped}” — Jira needs the address alone. {JIRA_SITE_HINT}</>
    );
  return (
    <Field
      id="jira-base-url"
      label="Jira site address"
      inputMode="url"
      required
      placeholder="https://rochwall.atlassian.net"
      hint={hint}
      error={siteError}
      value={fields.baseUrl}
      onChange={(event) => onType("baseUrl", event.target.value)}
      onBlur={(event) => {
        if (event.target.value.trim() !== "") onLeave(event.target.value);
      }}
    />
  );
}

/** The space key, typed. `onLeave` is for the flow, which reads that space's work types next (#600). */
export function JiraSpaceKeyField({ fields, onType, onLeave }: Holding & { onLeave?: (typed: string) => void }) {
  return (
    <Field id="jira-project-key" label="Space key" required placeholder="TAT" hint="The letters before the number in a work item key: TAT in TAT-7" value={fields.projectKey} onChange={(event) => onType("projectKey", event.target.value)} onBlur={onLeave === undefined ? undefined : (event) => onLeave(event.target.value)} />
  );
}

/** The work type, typed. */
export function JiraWorkTypeField({ fields, onType }: Holding) {
  return (
    <Field id="jira-issue-type" label="Work type" required placeholder="Task" hint="Must match a work type in that space exactly, e.g. Task" value={fields.issueType} onChange={(event) => onType("issueType", event.target.value)} />
  );
}

/** The labels, which are typed wherever they are asked for. */
export function JiraLabelsField({ fields, onType }: Holding) {
  return (
    <Field id="jira-labels" label="Labels (comma-separated, optional)" placeholder="torque, aws" hint="Single words; separate with commas" value={fields.labels} onChange={(event) => onType("labels", event.target.value)} />
  );
}

/**
 * Where work items go, typed: the space, the work type, the labels.
 *
 * What the flow falls back to where it has no lists to read (`JiraFlow.tsx`, #600).
 */
export function JiraDestinationFields({ fields, onType }: Holding) {
  return (
    <>
      <JiraSpaceKeyField fields={fields} onType={onType} />
      <JiraWorkTypeField fields={fields} onType={onType} />
      <JiraLabelsField fields={fields} onType={onType} />
    </>
  );
}

/**
 * One of Jira's own lists to choose from (#600), under the label and the id the typed
 * field has: it is the same setting, and only one of the two is ever on the page.
 */
export function JiraChoice({
  id,
  label,
  placeholder,
  hint,
  options,
  value,
  onChoose,
}: {
  id: string;
  label: string;
  /** The option that is no choice, shown until one is made. */
  placeholder: string;
  hint?: ReactNode;
  options: ReadonlyArray<{ value: string; text: string }>;
  value: string;
  onChoose: (value: string) => void;
}) {
  const hintId = hint === undefined ? undefined : `${id}-hint`;
  return (
    <p className="field">
      <label htmlFor={id} className="field__label">
        {label}
      </label>
      <select id={id} className="input" required aria-describedby={hintId} value={value} onChange={(event) => onChoose(event.target.value)}>
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.text}
          </option>
        ))}
      </select>
      {hint === undefined ? null : (
        <small id={hintId} className="field__hint">
          {hint}
        </small>
      )}
    </p>
  );
}

/** The account and its token: both asked for, whether connecting or replacing a saved token (#817). */
export function JiraCredentialFields({ fields, onType }: Holding) {
  return (
    <>
      <Field
        id="jira-email"
        label="Atlassian account email"
        type="email"
        required
        name="jira-account-email"
        autoComplete="off"
        hint="The email you sign in to Atlassian with; the token below must belong to this account"
        value={fields.email}
        onChange={(event) => onType("email", event.target.value)}
      />
      <Field
        id="jira-api-token"
        label="Atlassian API token"
        type="password"
        required
        name="jira-api-token"
        autoComplete="new-password"
        reveal
        hint={ATLASSIAN_TOKEN_HINT}
        value={fields.token}
        onChange={(event) => onType("token", event.target.value)}
      />
    </>
  );
}
